import useTranslation from 'next-translate/useTranslation';
import { z } from 'zod';

import { nameRegex, specialCharactersRegex } from '~/components/shared/Auth/utils';
import { useIsBlockedLanguage } from '~/domains/common/hooks/useIsBlockedLanguage';
import { Countries } from '~/common/constants/countries.enum';
import { validateAgeRestriction, validateDay } from '~/domains/kyc/utils/validation';
import { CANADA_POSTAL_CODE_REGEX, USA_ZIP_CODE_REGEX } from '~/domains/kyc/utils/zipCodeSchemas';
import { zipMatchesUsState } from '~/domains/kyc/utils/zipStateMatch';
import { STATE_PROVINCE_CODES } from '~/domains/auth/consts';

const useGenericStringConstraint = () => {
  const { t } = useTranslation('user');

  return (name: string) =>
    z
      .string({
        required_error: t('kyc.form.validation.required', {
          name,
        }),
        invalid_type_error: t('kyc.form.validation.invalid', {
          name,
        }),
      })
      .trim()
      .min(
        2,
        t('kyc.form.validation.stringLength', {
          name,
          length: 2,
        })
      )
      .regex(
        nameRegex,
        t('kyc.form.validation.letters', {
          name,
        })
      )
      .nonempty(
        t('kyc.form.validation.required', {
          name,
        })
      );
};

export const EmailObject = () => {
  const { t } = useTranslation('auth');
  return z
    .string()
    .trim()
    .email({ message: t('common.fieldIsInvalid', { field: 'Email' }) });
};

export const PasswordObject = () => {
  const { t } = useTranslation('auth');
  return z
    .string()
    .min(8, { message: t('common.fieldIsInvalid', { field: 'Password' }) })
    .max(256, { message: t('common.fieldIsInvalid', { field: 'Password' }) })
    .regex(/[0-9]/, { message: t('common.fieldIsInvalid', { field: 'Password' }) })
    .regex(/[a-z]/, { message: t('common.fieldIsInvalid', { field: 'Password' }) })
    .regex(/[A-Z]/, { message: t('common.fieldIsInvalid', { field: 'Password' }) })
    .regex(specialCharactersRegex, { message: t('common.fieldIsInvalid', { field: 'Password' }) });
};

export const useUserSchema = () => {
  const { t } = useTranslation('auth');
  const genericStringConstraint = useGenericStringConstraint();

  const validateBlockedLanguage = useIsBlockedLanguage();

  // Email & Referral Code (Sign Up V2 - Step 1)
  const email = EmailObject();

  const promoOrReferralCode = z
    .string()
    .optional()
    .or(z.literal(''));

  // Password (Sign Up V2 - Step 2)
  const password = PasswordObject();

  // Handle (Sign Up V2 - Step 3)
  const handle = z
    .string()
    .trim()
    .min(3, { message: t('signUp.handle.requirements.tooShort') })
    .max(20, { message: t('signUp.handle.requirements.tooLong') })
    .regex(/^[a-zA-Z0-9._-]+$/, { message: t('signUp.handle.requirements.notAllowedChars') })
    .regex(/^[a-zA-Z0-9]/, { message: t('signUp.handle.requirements.startsEndsSpecialChars') })
    .regex(/[a-zA-Z0-9]$/, { message: t('signUp.handle.requirements.startsEndsSpecialChars') })
    .refine((value) => /[a-zA-Z]/.test(value), {
      message: t('signUp.handle.requirements.atLeastOneLetter'),
    })
    .refine((value) => !/\s/.test(value), {
      message: t('signUp.handle.requirements.containsWhiteSpaces'),
    })
    .refine((value) => !/(?=.*[._-]{2})/.test(value), {
      message: t('signUp.handle.requirements.hasConsecutiveSpecialChars'),
    });

  // Name (Sign Up V1)
  const name = z
    .string()
    .trim()
    .min(4, { message: 'Minimum 4 characters' })
    .max(50, { message: 'Maximum 50 characters' })
    .regex(/^[A-Za-z\s\-']+$/, {
      message: 'Only letters, spaces, hyphens, and apostrophes are allowed.',
    })
    .refine(
      (value) => validateBlockedLanguage(value, t('commissioner:onboarding.name.label')).isValid
    );

  const description = z
    .string()
    .trim()
    .max(80, { message: 'Maximum 80 characters' })
    .refine(
      (value) =>
        validateBlockedLanguage(value, t('commissioner:onboarding.description.label')).isValid
    );

  // Name and DOB (KYC - Step 4 - V2)
  const firstName = genericStringConstraint(t('signUp.firstName.label'))
    .trim()
    .min(1, { message: t('signUp.firstName.requirements.required') });

  const lastName = genericStringConstraint(t('signUp.lastName.label'))
    .trim()
    .min(1, { message: t('signUp.lastName.requirements.required') });

  // Date of Birth
  const day = z
    .string()
    .regex(/^\d{1,2}$/, { message: t('signUp.dob.day.requirements.invalid') })
    .refine(
      (value) => {
        const dayValue = parseInt(value, 10);
        return dayValue > 0 && dayValue <= 31;
      },
      {
        message: t('signUp.dob.day.requirements.invalid'),
      }
    );
  const month = z.string().superRefine((value, ctx) => {
    if (!/^\d{1,2}$/.test(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('signUp.dob.month.requirements.numeric'),
      });
    }
    const monthValue = parseInt(value, 10);

    if (monthValue <= 0 || monthValue > 12) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('signUp.dob.month.requirements.invalid'),
      });
    }
  });

  const year = z
    .string()
    .regex(/^\d{4}$/, { message: t('signUp.dob.year.requirements.invalid') })
    .refine(
      (value) => {
        const yearValue = parseInt(value, 10);
        const currentYear = new Date().getFullYear();
        return yearValue > 1900 && yearValue <= currentYear;
      },
      {
        message: t('signUp.dob.year.requirements.invalid'),
      }
    );

  const dob = z
    .object({
      day,
      month,
      year,
    })
    .superRefine(({ day: thisDay, month: thisMonth, year: thisYear }, ctx) => {
      // Check if any of the individual fields have validation errors
      const hasFieldErrors =
        !day.safeParse(thisDay).success ||
        !month.safeParse(thisMonth).success ||
        !year.safeParse(thisYear).success;

      // Only proceed with date validations if no field-level errors
      if (!hasFieldErrors) {
        const isValidDate = validateDay({ day: thisDay, month: thisMonth, year: thisYear });
        if (!isValidDate?.value) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('user:kyc.form.validation.invalidDateOfBirth'),
            path: ['day'],
          });
        }
        if (
          isValidDate?.value &&
          !validateAgeRestriction({ day: thisDay, month: thisMonth, year: thisYear })
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('user:kyc.form.validation.ageRestriction'),
            path: ['year'],
          });
        }
      }
    });

  const phone = z
    .string({
      required_error: t('user:kyc.form.validation.required', {
        name: t('signUp.phone.label'),
      }),
      invalid_type_error: t('user:kyc.form.validation.invalid', {
        name: t('signUp.phone.label'),
      }),
    })
    .nonempty(
      t('user:kyc.form.validation.required', {
        name: t('signUp.phone.label'),
      })
    )
    .length(
      10,
      t('user:kyc.form.validation.numLength', {
        name: t('signUp.phone.label'),
        length: 10,
      })
    )
    .regex(
      /^[0-9]\d{1,14}$/,
      t('user:kyc.form.validation.numericInput', {
        name: t('signUp.phone.label'),
      })
    );

  const address = z
    .string({
      required_error: t('user:kyc.form.validation.required', {
        name: t('signUp.address.label'),
      }),
      invalid_type_error: t('user:kyc.form.validation.invalid', {
        name: t('signUp.address.label'),
      }),
    })
    .nonempty(
      t('user:kyc.form.validation.required', {
        name: t('signUp.address.label'),
      })
    )
    .regex(
      /[a-zA-Z]/,
      t('user:kyc.form.validation.invalid', {
        name: t('signUp.address.label'),
      })
    );

  const city = genericStringConstraint(t('signUp.city.label'));

  const USASchema = z.object({
    state: genericStringConstraint(t('signUp.state.label.US'))
      .length(
        2,
        t('user:kyc.form.validation.exactStringLength', {
          name: t('signUp.state.label.US'),
          length: 2,
        })
      )
      .refine((value) => STATE_PROVINCE_CODES[Countries.US].includes(value.toUpperCase()), {
        message: t('signUp.state.US.error'),
      }),
    country: z.literal(Countries.US),
    zip: z.string().regex(USA_ZIP_CODE_REGEX, t('signUp.zip.error.US')),
  });

  const CanadaSchema = z.object({
    state: genericStringConstraint(t('signUp.state.label.US'))
      .length(
        2,
        t('user:kyc.form.validation.exactStringLength', {
          name: t('signUp.state.label.CA'),
          length: 2,
        })
      )
      .refine((value) => STATE_PROVINCE_CODES[Countries.CA].includes(value.toUpperCase()), {
        message: t('signUp.state.CA.error'),
      }),
    country: z.literal(Countries.CA),
    zip: z.string().regex(CANADA_POSTAL_CODE_REGEX, t('signUp.zip.error.CA')),
  });

  const kycSchema = z
    .object({
      firstName,
      lastName,
      dob,
      address,
      city,
      phone,
    })
    .and(z.union([USASchema, CanadaSchema]))
    .superRefine((values, ctx) => {
      if (values.country === Countries.US && !zipMatchesUsState(values.zip, values.state)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('signUp.zip.error.stateMismatch'),
          path: ['zip'],
        });
      }
    });

  const signUpSchema = z.object({
    name,
    handle,
    email,
    password,
    promoOrReferralCode,
    firstName,
    lastName,
  });

  const signInSchema = z.object({
    emailOrUsername: email,
    password: z
      .string()
      .trim()
      .min(
        1,
        t('common.fieldIsRequired', {
          field: t('signIn.password.label'),
        })
      ),
  });

  const editChannelSchema = z.object({
    name,
    handle,
    description,
  });

  const oauthSchema = z.object({
    handle,
    email,
    promoOrReferralCode,
  });

  return {
    kycSchema,
    oauthSchema,
    signUpSchema,
    editChannelSchema,
    signInSchema,
  };
};
